<template>
  <div>
    <header>
      <LeftSidePanel @minimized="handleMinimizedLeftSidePanel"></LeftSidePanel>
    </header>
    <main>
      <div class="hyp-home-right-container">
        <Map
          v-if="!hasWatchOnly && !hasSearchOnly"
          :id="mapId"
          :show-search-control="true"
          :show-search-with-keyword-control="false"
          @map-on-load="mapOnLoad"
          @map-on-move-zoom-end="mapOnMoveEnd"
        ></Map>

        <DetailsSidebar
          :left-panel-minimized="leftPanelMinimized"
        ></DetailsSidebar>
      </div>

      <Notifier></Notifier>
      <ExperimentalWarning />
      <WelcomeMessage></WelcomeMessage>
    </main>
    <footer v-if="isWhiteLabelUserComputed">
      <div class="hyp-footer">Powered by Seerist Inc.</div>
    </footer>
  </div>
</template>

<script>
import uuidV4 from "uuid/v4";
import lodashGet from "lodash/get";
import lodashIsEqual from "lodash/isEqual";
import lodashHas from "lodash/has";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashOrderBy from "lodash/orderBy";

import { mapGetters } from "vuex";

import {
  GET_STABILITY_REFRESH_MAP,
  GET_POWER_OUTAGE_REFRESH_MAP,
  GET_STABILITY_PARAMS,
  GET_HOTSPOTS_REFRESH_MAP,
  GET_TOPICS_V2_REFRESH_MAP,
  GET_TOPICS_V2_MAP_TOPIC,
  GET_NEWS_REFRESH_MAP,
  GET_SOCIAL_REFRESH_MAP,
  GET_MAP_REFRESH_DATA,
  GET_MAIN_MAP_MOVE_END,
  GET_ALERTS_REFRESH_MAP,
  GET_TRIGGERED_ALERTS,
  CLEAR_TRIGGERED_ALERTS,
  GET_PLACES_REFRESH_MAP,
  GET_DAY_NIGHT_OVERLAY_REFRESH_MAP,
  GET_SELECTED_DATES,
  GET_SELECTOR_PANEL,
  GET_PREFERENCES,
  SAVE_PREFERENCES,
  GET_SEARCH_LOCATION_REFRESH_MAP,
  GET_DETAILS_SIDEBAR,
  GET_MONITOR_ENABLED,
  ADD_TRIGGERED_ALERT,
  REMOVE_TRIGGERED_ALERT,
} from "@/store/actions.type";
import {
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_GRIDS,
  MODULE_PREFIX_HOTSPOTS,
  MODULE_PREFIX_TOPICS_V2,
  MODULE_PREFIX_NEWS,
  MODULE_PREFIX_SOCIAL,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_ALERTS_CONTENT,
  MODULE_PREFIX_ALERTS,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_SEARCH,
  MODULE_PREFIX_PLACES,
  MODULE_PREFIX_MONITOR,
  MODULE_PREFIX_DAY_NIGHT_OVERLAY,
  MODULE_PREFIX_POWER_OUTAGE,
} from "@/store";
import {
  getLevelsForInitialLoad as getStabilityLevelsForInitialLoad,
  getLevelsFromPreferences as getStabilityLevelsFromPreferences,
} from "@/services/grids/stability.service";
import {
  getTopicsArray,
  getInfluencesArray,
} from "@/services/events/topics.service";
import {
  getDefaultStartEndDates,
  getFormattedTime,
  now,
} from "@/common/date.utilities";
import dayjs from "dayjs";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { GRIDS_STABILITY_TYPES } from "@/common/constants/grids";
import {
  addNotificationItem,
  getNotificationItem,
  deleteAllNotificationItems,
} from "@/common/browserDb/alertNotifications";
import { getAccessToken } from "@/common/storage";
import {
  setupSavePreferences,
  hasWatchOnlyAccess,
  hasSearchOnlyAccess,
  isWhiteLabelUser,
  getPreferredLandingPage,
} from "@/services/user/user.service";
import { saveClickStreamEvent } from "@/services/common/feedbackLoop.service";
import { ENDPOINT_USER_PREFERENCES } from "@/common/constants/api";
import { X_API_KEY } from "@/common/config";
import { defaultSetUp as setUpNotifications } from "@/services/common/notification.service";
import { pageTrackerLogger } from "@/common/pageTracker";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { setTopicIcons } from "@/common/constants/icons";
import { setTopicIconsSVG } from "@/common/constants/topic_icons_svg";

import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  setTopicEventCategories,
} from "@/common/constants/analytics";
import { setTopicDetailSidebarTypes } from "@/common/constants/home";
import { setTopicsIcons } from "@/common/constants/maps";
import { setLabels } from "@/common/constants/labels";
import { setGridColors } from "@/common/constants/grids";
import { getPreference } from "@/services/powerOutage/powerOutage.service";

let LAST_TRIGGERED_ALERT_REFRESH = now;

export default {
  name: "Home",
  components: {
    LeftSidePanel: () => import("@/components/common/LeftSidePanel"),
    DetailsSidebar: () => import("@/components/common/details/DetailsSidebar"),
    Map: () => import("@/components/map/Map"),
    Notifier: () => import("@/components/common/Notifier"),
    ExperimentalWarning: () => import("@/components/auth/ExperimentalWarning"),
    WelcomeMessage: () => import("@/components/auth/WelcomeMessage"),
  },
  data: () => ({
    mapId: MAIN_MAP_ID,
    mapInitialLoad: false,
    autoDataRefresh: null,
    autoDataRefreshRateInMinutes: 15,
    autoAlertsRefresh: null,
    autoAlertsRefreshRateInMinutes: 1,
    alertNotificationTimeout: 15,
    alertInitialLoad: true,
    currentMapCenter: null,
    currentMapZoom: null,
    hasWatchOnly: hasWatchOnlyAccess(),
    hasSearchOnly: hasSearchOnlyAccess(),
    leftPanelMinimized: true,
  }),
  created() {
    window.addEventListener("beforeunload", this.saveBeforeExit);

    // setup some of the dynamic constants
    setTopicIcons();
    setTopicIconsSVG();
    setTopicEventCategories();
    setTopicDetailSidebarTypes();
    setTopicsIcons();
    setLabels();
    setGridColors();
  },
  async mounted() {
    this.$store.dispatch(MODULE_PREFIX_USER + GET_PREFERENCES);
    this.setSelectedDates();

    await setUpNotifications({
      notificationCallback: (res) => {
        if (!this.hasWatchOnly && !this.hasSearchOnly) {
          this.$store.dispatch(MODULE_PREFIX_ALERTS + ADD_TRIGGERED_ALERT, res);
          this.refreshMapAlerts();
        }
      },
    });

    if (!this.hasWatchOnly && !this.hasSearchOnly) {
      if (this.firebaseEnabled) {
        this.alertInitialLoad = false;
      }

      this.startAutoDataRefresh();
      this.startAutoAlertsRefresh();
      this.refreshTriggeredAlerts();

      // open the alerted content slideout if URL is set
      if (
        this.$route.name === "alerts" ||
        this.$route.name === "alertid" ||
        this.$route.name === "triggeredid"
      ) {
        let alert = {};
        const parts = this.$route.path.split("/");
        if (parts && Array.isArray(parts) && parts.length) {
          alert.alertId = parts[2] ? parts[2] : null;
          alert.triggeredId = parts[3] ? parts[3] : null;
        }
        this.openAlertedContent(alert);
      } else if (
        this.$route.name === "watch" ||
        this.$route.name === "watch-code"
      ) {
        this.openWatch();
      } else if (
        (this.$route.name === "latest" ||
          getPreferredLandingPage() === "latest") &&
        this.isLatestFeatureUnlocked
      ) {
        this.openLatest();
      }
    } else {
      this.openWatch();
    }
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
      refreshData: MODULE_PREFIX_MAP + "refreshData",
      selectedDates: MODULE_PREFIX_HOME + "selectedDates",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
      autoRefreshEnabled: MODULE_PREFIX_HOME + "autoRefreshEnabled",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      triggeredAlerts: MODULE_PREFIX_ALERTS + "triggered",
      firebaseEnabled: MODULE_PREFIX_HOME + "firebaseEnabled",
      topics: MODULE_PREFIX_TOPICS_V2 + "topics",
    }),
    alertIconSrc() {
      return require("@/assets/images/icons/alerts_big.png");
    },
    canViewAlertNotifications() {
      let alertNotificationsEnabled = true;
      if (lodashHas(this.preferences, "home.alertNotifications")) {
        alertNotificationsEnabled = lodashGet(
          this.preferences,
          "home.alertNotifications"
        );
      }
      return alertNotificationsEnabled;
    },
    isWhiteLabelUserComputed() {
      return isWhiteLabelUser();
    },
    isLatestFeatureUnlocked() {
      return this.featureUnlocked("latest-dashboard");
    },
  },
  methods: {
    handleMinimizedLeftSidePanel(minimized) {
      this.leftPanelMinimized = minimized;
    },
    saveBeforeExit(event) {
      // this function saves the current prefs and clickstream right before the
      // user closes the browser or changes the url. It uses the fetch
      // keepalive feature to make sure the call gets made even once
      // the page has been closed.

      // TODO: see if can incorporate this logic into the current
      // clickstream and savePreferences service functions

      // send user prefs
      const TOKEN = getAccessToken();
      const PREFS = setupSavePreferences();
      const BODY_PREFS = { preferences: PREFS };
      fetch(ENDPOINT_USER_PREFERENCES, {
        method: "POST",
        body: JSON.stringify(BODY_PREFS),
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${TOKEN}`,
          "x-api-key": X_API_KEY,
        },
      });

      saveClickStreamEvent({
        type: "USER_LOGOUT",
      });

      //TODO: This is probably overkill but we want this...
      pageTrackerLogger("Attempting to call pageTrackerSignin on page reload");
      this.pageTrackerSignout();
    },
    setSelectedDates() {
      const START_END_DATES = getDefaultStartEndDates();
      this.selectedDates.start = START_END_DATES.start;
      this.selectedDates.end = START_END_DATES.end;
      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTED_DATES,
        this.selectedDates
      );
    },
    mapOnLoad(map) {
      this.mapInitialLoad = true;
      let zoom = 1;
      if (lodashGet(this.preferences, "map.zoom")) {
        zoom = this.preferences.map.zoom;
      }

      let center = [0, 0];
      if (lodashGet(this.preferences, "map.center")) {
        center = [
          this.preferences.map.center.lng,
          this.preferences.map.center.lat,
        ];
      }

      map.flyTo({
        zoom: zoom,
        center: center,
      });
    },
    mapOnMoveEnd(map) {
      if (this.mapInitialLoad) {
        this.mapInitialLoad = false;
        this.setAllFromPreferences();
      } else {
        if (
          !lodashIsEqual(this.currentMapCenter, map.getCenter()) ||
          this.currentMapZoom !== map.getZoom()
        ) {
          if (!this.detailsSidebar.visible) {
            this.refreshMapAll();
            this.saveMapPrefs(map);
          }
        }
      }
      this.currentMapCenter = map.getCenter();
      this.currentMapZoom = map.getZoom();

      this.$store.dispatch(
        MODULE_PREFIX_MAP + GET_MAIN_MAP_MOVE_END,
        new Date().toISOString()
      );
    },
    setAllFromPreferences() {
      this.setStabilityStateFromPreferences();
      this.setHotspotsStateFromPreferences();
      this.setTopicsStateFromPreferences();
      this.setNewsStateFromPreferences();
      this.setSocialStateFromPreferences();
      this.setPlacesStateFromPreferences();
      this.setDayNightStateFromPreferences();
      this.setPowerOutageStateFromPreferences();
    },
    refreshMapAll() {
      this.$store.dispatch(
        MODULE_PREFIX_GRIDS + GET_STABILITY_REFRESH_MAP,
        true
      );
      this.$store.dispatch(
        MODULE_PREFIX_HOTSPOTS + GET_HOTSPOTS_REFRESH_MAP,
        true
      );
      this.$store.dispatch(MODULE_PREFIX_NEWS + GET_NEWS_REFRESH_MAP, true);
      this.$store.dispatch(MODULE_PREFIX_SOCIAL + GET_SOCIAL_REFRESH_MAP, true);
      this.$store.dispatch(MODULE_PREFIX_PLACES + GET_PLACES_REFRESH_MAP, true);
      this.$store.dispatch(
        MODULE_PREFIX_DAY_NIGHT_OVERLAY + GET_DAY_NIGHT_OVERLAY_REFRESH_MAP,
        true
      );
      this.$store.dispatch(
        MODULE_PREFIX_POWER_OUTAGE + GET_POWER_OUTAGE_REFRESH_MAP,
        true
      );

      this.refreshTopics();
    },
    refreshTopics() {
      const refresh = new Date().toISOString();
      const TOPICS_ARRAY = getTopicsArray();
      TOPICS_ARRAY.forEach((topic) => {
        const field = topic.parentIds ? "apiName" : "type";
        const type = topic[field];
        if (this.topics && this.topics[type]) {
          let enabled = false;
          if (
            topic.parentIds ||
            (!topic.parentIds && topic.subtopics.length === 0)
          ) {
            enabled = this.topics[type].enabled || false;
          }
          this.$store.dispatch(
            MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_MAP_TOPIC,
            {
              type: type,
              enabled: enabled,
              refresh: refresh,
            }
          );
        }
      });
      this.$store.dispatch(
        MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_REFRESH_MAP,
        refresh
      );

      if (this.featureUnlocked("influences")) {
        const INFLUENCES_ARRAY = getInfluencesArray();
        INFLUENCES_ARRAY.forEach((topic) => {
          if (this.topics && this.topics[topic.apiName]) {
            let enabled = this.topics[topic.apiName].enabled || false;
            this.$store.dispatch(
              MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_MAP_TOPIC,
              {
                type: topic.apiName,
                enabled: enabled,
                refresh: refresh,
              }
            );
          }
        });
        this.$store.dispatch(
          MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_REFRESH_MAP,
          refresh
        );
      }
    },
    refreshMapAlerts() {
      this.$store.dispatch(
        MODULE_PREFIX_ALERTS_CONTENT + GET_ALERTS_REFRESH_MAP,
        true
      );
    },
    refreshTriggeredAlerts() {
      if (this.canViewAlertNotifications && !this.firebaseEnabled) {
        const PARAMS = {
          start: LAST_TRIGGERED_ALERT_REFRESH,
          end: this.selectedDates.end,
          bounds: "-90,-180,90,180",
          append: true,
        };

        LAST_TRIGGERED_ALERT_REFRESH = dayjs().toISOString();
        this.$store.dispatch(
          MODULE_PREFIX_ALERTS + GET_TRIGGERED_ALERTS,
          PARAMS
        );
      }
    },
    refreshSearchLocation() {
      this.$store.dispatch(
        MODULE_PREFIX_SEARCH + GET_SEARCH_LOCATION_REFRESH_MAP,
        true
      );
    },
    timeOnChange() {
      this.refreshMapAll();
    },
    setPowerOutageStateFromPreferences() {
      let enabled = false;
      if (lodashGet(this.preferences, "powerOutage")) {
        enabled = lodashGet(this.preferences, "powerOutage.enabled")
          ? lodashGet(this.preferences, "powerOutage.enabled")
          : null;
      }
      this.$store.dispatch(
        MODULE_PREFIX_POWER_OUTAGE + GET_POWER_OUTAGE_REFRESH_MAP,
        enabled
      );
    },
    setStabilityStateFromPreferences() {
      let params = {};
      params.type = GRIDS_STABILITY_TYPES.country;
      params.levels = getStabilityLevelsForInitialLoad();

      if (lodashGet(this.preferences, "grids.stability")) {
        params.type = lodashGet(this.preferences, "grids.stability.type")
          ? lodashGet(this.preferences, "grids.stability.type")
          : null;
        params.levels = getStabilityLevelsFromPreferences(this.preferences);
        params.local = this.preferences.grids.stability.hasOwnProperty("local")
          ? this.preferences.grids.stability.local
          : false;
      }
      this.$store.dispatch(MODULE_PREFIX_GRIDS + GET_STABILITY_PARAMS, params);
      this.$store.dispatch(
        MODULE_PREFIX_GRIDS + GET_STABILITY_REFRESH_MAP,
        !!params.type
      );
    },
    setHotspotsStateFromPreferences() {
      let enabled = false;
      if (lodashGet(this.preferences, "grids.hotspots.enabled")) {
        enabled = true;
      }
      if (lodashGet(this.preferences, "grids.hotspotsCovid.enabled")) {
        enabled = true;
      }
      this.$store.dispatch(
        MODULE_PREFIX_HOTSPOTS + GET_HOTSPOTS_REFRESH_MAP,
        enabled
      );
    },
    setDayNightStateFromPreferences() {
      const ENABLED = this.preferences.dayNightLayerShow;
      this.$store.dispatch(
        MODULE_PREFIX_DAY_NIGHT_OVERLAY + GET_DAY_NIGHT_OVERLAY_REFRESH_MAP,
        ENABLED
      );
    },
    setTopicsStateFromPreferences() {
      let params = {};
      const refresh = new Date().toISOString();
      const TOPICS_ARRAY = getTopicsArray();
      TOPICS_ARRAY.forEach((topic) => {
        const field = topic.parentIds ? "apiName" : "type";
        const type = topic[field];

        params.enabled = false;
        if (
          topic.parentIds ||
          (!topic.parentIds && topic.subtopics.length === 0)
        ) {
          params.enabled = lodashGet(this.preferences, `topics.${type}.enabled`)
            ? lodashGet(this.preferences, `topics.${type}.enabled`)
            : false;
        }

        this.$store.dispatch(
          MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_MAP_TOPIC,
          {
            type: type,
            enabled: params.enabled,
            refresh: refresh,
          }
        );
      });
      this.$store.dispatch(
        MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_REFRESH_MAP,
        refresh
      );

      if (this.featureUnlocked("influences")) {
        const INFLUENCES_ARRAY = getInfluencesArray();
        INFLUENCES_ARRAY.forEach((topic) => {
          params.enabled = lodashGet(
            this.preferences,
            `topics.${topic.apiName}.enabled`
          )
            ? lodashGet(this.preferences, `topics.${topic.apiName}.enabled`)
            : false;

          this.$store.dispatch(
            MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_MAP_TOPIC,
            {
              type: topic.apiName,
              enabled: params.enabled,
              refresh: refresh,
            }
          );
        });
        this.$store.dispatch(
          MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_REFRESH_MAP,
          refresh
        );
      }
    },
    setNewsStateFromPreferences() {
      const ENABLED = lodashGet(this.preferences, "media.news.enabled")
        ? lodashGet(this.preferences, "media.news.enabled")
        : false;

      this.$store.dispatch(MODULE_PREFIX_NEWS + GET_NEWS_REFRESH_MAP, ENABLED);
    },
    setSocialStateFromPreferences() {
      const ENABLED = lodashGet(this.preferences, "media.social.enabled")
        ? lodashGet(this.preferences, "media.social.enabled")
        : false;

      this.$store.dispatch(
        MODULE_PREFIX_SOCIAL + GET_SOCIAL_REFRESH_MAP,
        ENABLED
      );
    },
    setPlacesStateFromPreferences() {
      const PLACES = lodashGet(this.preferences, "places");
      let exists = false;
      if (PLACES) {
        PLACES.forEach((place) => {
          if (place.mapped) {
            exists = true;
          }
        });
      }

      this.$store.dispatch(
        MODULE_PREFIX_PLACES + GET_PLACES_REFRESH_MAP,
        exists
      );
    },
    saveMapPrefs(map) {
      let preferences = lodashCloneDeep(this.preferences);
      preferences.map = preferences.map || {};
      preferences.map.zoom = map.getZoom();
      const CENTER = map.getCenter();
      preferences.map.center = { lat: CENTER.lat, lng: CENTER.lng };
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
    },
    async saveAlertNotificationPrefs() {
      deleteAllNotificationItems();
      let preferences = lodashCloneDeep(this.preferences);
      preferences.home = preferences.home || {};
      if (!preferences.home.hasOwnProperty("alertNotifications")) {
        preferences.home.alertNotifications = true;
      }
      await this.$store.dispatch(
        MODULE_PREFIX_USER + SAVE_PREFERENCES,
        preferences
      );
    },
    startAutoDataRefresh() {
      console.log("Starting ALL DATA auto refresh");
      this.autoDataRefresh = setInterval(() => {
        this.refreshMapAll();
        this.$store.dispatch(MODULE_PREFIX_MAP + GET_MAP_REFRESH_DATA, false);
        console.log("ALL DATA auto refreshed", new Date());
      }, this.autoDataRefreshRateInMinutes * 60 * 1000);
    },
    stopAutoDataRefresh() {
      if (this.autoDataRefresh) {
        console.log("Stopping ALL DATA auto refresh");
        clearInterval(this.autoDataRefresh);
        this.autoDataRefresh = null;
      }
    },
    startAutoAlertsRefresh() {
      if (this.firebaseEnabled) {
        return;
      }
      console.log("Starting ALERTS auto refresh");
      this.autoAlertsRefresh = setInterval(() => {
        this.refreshMapAlerts();
        this.refreshTriggeredAlerts();
        console.log("ALERTS auto refreshed", new Date());
      }, this.autoAlertsRefreshRateInMinutes * 60 * 1000);
    },
    stopAutoAlertsRefresh() {
      if (this.autoAlertsRefresh) {
        console.log("Stopping ALERTS auto refresh");
        clearInterval(this.autoAlertsRefresh);
        this.autoAlertsRefresh = null;
      }
    },
    getAlertNotificationHtml(alert) {
      const time = getFormattedTime(
        new Date(alert.timestamp_ms).toISOString(),
        { hideSeconds: true }
      );
      const text = alert.message;
      const triggeredId = alert.triggeredId;
      return `
        <div>
          <div class="hyp-notification-img-container pull-left">
            <img src="${this.alertIconSrc}" class="pull-left hyp-notification-img">
          </div>
          <div class="hyp-notification-text-container pull-left">
            <div>
              <strong class="hyp-notification-title">Alert!</strong>
              <em class="pull-right hyp-notification-time">${time}</em>
            </div>
            <div class="snotifyToast__body">
              ${text} <span style="display:none">${triggeredId}</span>
            </div>
          </div>
          <div class="clearfix"></div>
        </div>
      `;
    },
    async openAlertedContent(alert) {
      let preferences = this.preferences || {};
      preferences.home = this.preferences.home || {};
      preferences.home.leftSidebar = this.preferences.home.leftSidebar || {};
      if (!preferences.home.leftSidebar.visible) {
        preferences.home.leftSidebar.visible = true;
        await this.$store.dispatch(
          MODULE_PREFIX_USER + SAVE_PREFERENCES,
          preferences
        );
      }

      this.selectorPanel.selected = "alerts";
      this.selectorPanel.selectedData = {};
      this.selectorPanel.selectedData.alertId = alert.alertId
        ? alert.alertId
        : null;
      this.selectorPanel.selectedData.triggeredId = alert.triggeredId
        ? alert.triggeredId
        : null;
      this.selectorPanel.visible = true;
      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
        this.selectorPanel
      );
    },
    async openWatch() {
      let type = GRIDS_STABILITY_TYPES.country;
      let code = null;
      if (lodashGet(this.$route, "params.code")) {
        code = this.$route.params.code;
        if (code.indexOf("-") > -1) {
          type = GRIDS_STABILITY_TYPES.city;
        } else if (code.length > 2) {
          type = GRIDS_STABILITY_TYPES.region;
        }
      }
      this.detailsSidebar.visible = true;
      this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.monitor;
      this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.monitor;
      this.detailsSidebar.monitorType = DETAIL_SIDEBAR_TYPES.stability;
      this.detailsSidebar.stabilityType = type;
      this.detailsSidebar.code = code;
      this.detailsSidebar.openMonitor = true;
      this.$store.dispatch(MODULE_PREFIX_MONITOR + GET_MONITOR_ENABLED, true);
      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );
    },
    openLatest() {
      this.selectorPanel.selected = "latest";
    },
    pageTrackerSignout() {
      pageTrackerLogger("_pageTrackerSignout");
      let _paq = (window._paq = window._paq || []);
      const profile = JSON.parse(localStorage.getItem("user_profile"));
      if (profile && profile.email) {
        const userId = profile.email;
        _paq.push([
          "trackEvent",
          EVENT_CATEGORIES.system,
          EVENT_ACTIONS.loggedOut,
          userId,
        ]);
        pageTrackerLogger(
          "Page Tracker Event " +
            EVENT_CATEGORIES.system +
            " " +
            EVENT_ACTIONS.loggedOut +
            " " +
            userId +
            " logged."
        );
      }
    },
  },
  watch: {
    refreshData: {
      handler() {
        if (this.refreshData) {
          this.refreshMapAll();
          this.refreshMapAlerts();
          this.refreshTriggeredAlerts();
          this.refreshSearchLocation();
          this.$store.dispatch(MODULE_PREFIX_MAP + GET_MAP_REFRESH_DATA, false);
        }
      },
    },
    autoRefreshEnabled: {
      handler() {
        if (this.autoRefreshEnabled) {
          this.startAutoDataRefresh();
          this.startAutoAlertsRefresh();
        } else {
          this.stopAutoDataRefresh();
          this.stopAutoAlertsRefresh();
        }
      },
    },
    triggeredAlerts: {
      async handler() {
        if (
          this.canViewAlertNotifications &&
          this.triggeredAlerts &&
          Array.isArray(this.triggeredAlerts) &&
          this.triggeredAlerts.length
        ) {
          const SORTED_ALERTS = lodashOrderBy(
            this.triggeredAlerts,
            ["timestamp_ms_utc"],
            ["asc"]
          );

          const LEN2 = SORTED_ALERTS.length;
          if (LEN2 > 0 && !this.alertInitialLoad) {
            this.$snotify.clear();
          }
          for (let i = 0; i < LEN2; i++) {
            const alert = SORTED_ALERTS[i];
            const NOTIFIED_ALERT = await getNotificationItem({
              alertId: alert.alertId,
              timestampMsUtc: alert.timestamp_ms_utc,
            });
            if (!NOTIFIED_ALERT) {
              // dont show any notifications on the initial load of the page
              if (!this.alertInitialLoad) {
                const ALERT_HTML = this.getAlertNotificationHtml(alert);
                const ID = uuidV4();
                const TOASTED = this.$snotify.confirm(
                  alert.triggeredId,
                  "Alert!",
                  {
                    id: ID,
                    html: ALERT_HTML,
                    timeout: this.alertNotificationTimeout * 60 * 1000,
                    showProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    position: "rightTop",
                    buttons: [
                      {
                        text: "Dismiss All",
                        action: () => {
                          const clearSnotify = () => {
                            this.$snotify.clear();
                            let element = document.querySelector(
                              ".hyp-notification-title"
                            );
                            if (element) {
                              setTimeout(() => {
                                clearSnotify();
                              }, 100);
                            }
                          };
                          clearSnotify();
                          this.$store.dispatch(
                            MODULE_PREFIX_ALERTS + CLEAR_TRIGGERED_ALERTS,
                            []
                          );
                        },
                        className: `hyp-dismiss-${ID} hyp-notify-btn-dismiss-all`,
                      },
                      {
                        text: "Dismiss",
                        action: (toast) => {
                          this.$snotify.remove(toast.id);
                          this.$store.dispatch(
                            MODULE_PREFIX_ALERTS + REMOVE_TRIGGERED_ALERT,
                            toast.body
                          );
                        },
                      },
                    ],
                  }
                );

                TOASTED.on("click", (toast) => {
                  this.openAlertedContent(alert);
                  this.$snotify.remove(toast.id);
                  this.$store.dispatch(
                    MODULE_PREFIX_ALERTS + REMOVE_TRIGGERED_ALERT,
                    toast.body
                  );
                });
                TOASTED.on("mouseenter", (toast) => {
                  const ELEM = document.querySelector(
                    `.hyp-dismiss-${toast.id}`
                  );
                  ELEM.style.visibility = "visible";
                });
                TOASTED.on("mouseleave", (toast) => {
                  const ELEM = document.querySelector(
                    `.hyp-dismiss-${toast.id}`
                  );
                  ELEM.style.visibility = "hidden";
                });
              }
              await addNotificationItem({
                alertId: alert.alertId,
                message: alert.message,
                timestampMsUtc: alert.timestamp_ms_utc,
              });
            }
          }
          this.alertInitialLoad = false;
        }
      },
    },
    firebaseEnabled: {
      handler(enabled) {
        if (enabled) {
          this.startAutoAlertsRefresh();
        } else {
          this.stopAutoDataRefresh();
        }
      },
    },
  },
  beforeDestroy() {
    this.stopAutoDataRefresh();
    this.stopAutoAlertsRefresh();
  },
};
</script>

<style scoped>
.hyp-home-right-container {
  float: left;
}
.map-control-container {
  padding: 0;
  position: absolute;
  bottom: 30px;
  right: 65px;
  pointer-events: none;
}
.home-info-viewer {
  width: fit-content;
  float: right;
  margin-left: 8px;
}
.xs1-overrides {
  max-width: fit-content !important;
  padding: 4px 0 !important;
  pointer-events: auto;
}
</style>

<style>
.hyp-notification-text-container {
  width: 230px;
  margin-left: 10px;
}
.hyp-notification-img-container {
  width: 30px;
  text-align: center;
}
.hyp-notification-img {
  width: 30px;
  height: 30px;
  text-align: center;
  vertical-align: middle;
}
.hyp-notification-title {
  font-size: 14px;
  font-family: "TruenoRg";
}
.hyp-notification-time {
  color: gray;
  font-size: 11px;
  margin-top: 5px;
}
.snotify {
  z-index: 5 !important;
}
.snotifyToast {
  border-radius: 9px;
}
.snotifyToast__buttons,
.snotifyToast__buttons button {
  border: none !important;
  font-size: 12px;
}
.snotifyToast__title {
  font-size: 14px;
  font-family: "TruenoRg";
}
.snotifyToast__body {
  font-size: 12px;
}
.snotifyToast__inner {
  padding: 5px 15px;
}
.snotifyToast__progressBar {
  height: 2px;
}
.snotifyToast__progressBar__percentage {
  height: 2px;
}
.snotify-confirm {
  border-left: 4px solid #f82e7f;
}
.snotify-confirm .snotifyToast__inner,
.snotify-prompt .snotifyToast__inner {
  padding: 5px 5px !important;
}
.snotifyToast__buttons button {
  padding: 4px !important;
}
.hyp-notify-btn-dismiss-all {
  visibility: hidden;
}
.hyp-footer {
  bottom: 0px;
  position: absolute;
  z-index: 300;
  width: 100%;

  background-color: #2a2a2a;
  color: white;
  font-size: 12px;
  font-family: "TruenoLt";
  padding: 5px;
}
</style>
